const DATE_PATTERN = /^(\d{4})(\d{2})(\d{2})$/;
const DEFAULT_RESULTS_BATCH_SIZE = 20;

function normalizeTrademark(value) {
    return String(value || '')
        .normalize('NFKD')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '');
}

function prioritizeExactMatches(matches, searchedMark) {
    const normalizedSearch = normalizeTrademark(searchedMark);
    if (!normalizedSearch) {
        return { exactMatches: [], orderedMatches: [...matches] };
    }

    const exactMatches = [];
    const similarMatches = [];

    matches.forEach((match) => {
        const markLiteral = match?.match_data?.mark_literal;
        const target = normalizeTrademark(markLiteral) === normalizedSearch
            ? exactMatches
            : similarMatches;
        target.push(match);
    });

    return { exactMatches, orderedMatches: [...exactMatches, ...similarMatches] };
}

function formatFiledDate(value) {
    const match = DATE_PATTERN.exec(String(value || ''));
    if (!match) {
        return value || '—';
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (date.getUTCFullYear() !== year
        || date.getUTCMonth() !== month - 1
        || date.getUTCDate() !== day
    ) {
        return value;
    }

    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
        year: 'numeric',
    }).format(date);
}

function formatStatus(value) {
    return String(value || '').replace(/^live\s*\/\s*/i, '');
}

function appendResultCell(row, label, value, className = '') {
    const cell = document.createElement('td');
    cell.dataset.label = label;
    if (className) {
        cell.className = className;
    }

    const text = document.createElement('span');
    text.textContent = value || '—';
    cell.append(text);
    row.append(cell);

    return cell;
}

function templateHasContent(template) {
    return Boolean(template.content.textContent.trim() || template.content.querySelector('img, video, iframe'));
}

function createExactResultsDisclaimer(template) {
    if (!template || !templateHasContent(template)) {
        return null;
    }

    const disclaimer = document.createElement('div');
    disclaimer.className = 'trademark-search__exact-results-disclaimer';
    disclaimer.append(template.content.cloneNode(true));

    return disclaimer;
}

function initializeExactSearchTooltip(search) {
    const trigger = search.querySelector('[data-trademark-search-exact-tooltip-trigger]');
    const modal = search.querySelector('[data-trademark-search-exact-tooltip]');
    const closeButton = modal?.querySelector('[data-trademark-search-exact-tooltip-close]');

    if (!trigger || !modal || !closeButton) {
        return;
    }

    trigger.addEventListener('click', () => {
        trigger.setAttribute('aria-expanded', 'true');
        modal.showModal();
    });

    closeButton.addEventListener('click', () => modal.close());
    modal.addEventListener('click', (event) => {
        if (event.target === modal) {
            modal.close();
        }
    });
    modal.addEventListener('close', () => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
    });
}

function createRegistrationLink(registrationUrl, registrationLabel, registrationStyle, searchedMark) {
    if (!registrationUrl || !registrationLabel) {
        return null;
    }

    const registrationLinkUrl = new URL(registrationUrl, window.location.origin);
    registrationLinkUrl.searchParams.set('trademark', searchedMark);

    const button = document.createElement('div');
    button.className = `wp-block-button is-style-${registrationStyle}`;

    const link = document.createElement('a');
    link.className = 'wp-block-button__link wp-element-button';
    link.href = registrationLinkUrl.toString();
    link.textContent = registrationLabel;

    const arrow = document.createElement('span');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    link.append(arrow);
    button.append(link);

    return button;
}

function createResultRow(match) {
    const data = match && typeof match.match_data === 'object' ? match.match_data : {};
    const row = document.createElement('tr');
    appendResultCell(row, 'Mark', data.mark_literal, 'trademark-search__mark');
    appendResultCell(row, 'Details', data.serial_number);

    const statusCell = appendResultCell(
        row,
        'Status',
        formatStatus(data.status),
        'trademark-search__status'
    );
    statusCell.dataset.status = String(data.status || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');

    const trademarkClass = [data.international_class, data.international_class_label]
        .filter(Boolean)
        .join(' - ');
    appendResultCell(row, 'Class', trademarkClass ? `Class ${trademarkClass}` : '—');
    appendResultCell(row, 'Filed On', formatFiledDate(data.filed_date));

    return row;
}

function appendResultRows(body, matches, start, end) {
    const rows = matches.slice(start, end).map(createResultRow);
    body.append(...rows);
}

function createViewMoreControls(body, matches, batchSize) {
    let visibleResults = batchSize;
    const controls = document.createElement('div');
    controls.className = 'trademark-search__view-more-wrap';

    const button = document.createElement('button');
    button.className = 'trademark-search__view-more';
    button.type = 'button';
    button.textContent = 'View More';

    const status = document.createElement('p');
    status.className = 'sr-only';
    status.setAttribute('aria-live', 'polite');

    button.addEventListener('click', () => {
        const previousCount = visibleResults;
        visibleResults = Math.min(visibleResults + batchSize, matches.length);
        appendResultRows(body, matches, previousCount, visibleResults);
        status.textContent = `${visibleResults - previousCount} more results shown. ${visibleResults} of ${matches.length} results are visible.`;

        if (visibleResults === matches.length) {
            button.remove();
            controls.classList.add('is-complete');
        }
    });

    controls.append(button, status);

    return controls;
}

// Same window as WordPress paginate_links(), which the query loop uses.
const PAGINATION_END_SIZE = 1;
const PAGINATION_MID_SIZE = 2;

function getPaginationItems(currentPage, pageCount) {
    const items = [];
    let showDots = false;

    for (let page = 1; page <= pageCount; page += 1) {
        const isEdge = page <= PAGINATION_END_SIZE || page > pageCount - PAGINATION_END_SIZE;
        const isNearCurrent = page >= currentPage - PAGINATION_MID_SIZE
            && page <= currentPage + PAGINATION_MID_SIZE;

        if (page === currentPage || isEdge || isNearCurrent) {
            items.push(page);
            showDots = true;
            continue;
        }

        if (showDots) {
            items.push('dots');
            showDots = false;
        }
    }

    return items;
}

function createPageNumber(page, currentPage, onSelect) {
    if (page === 'dots') {
        const dots = document.createElement('span');
        dots.className = 'page-numbers dots';
        dots.textContent = '…';
        dots.setAttribute('aria-hidden', 'true');
        return dots;
    }

    if (page === currentPage) {
        const current = document.createElement('span');
        current.className = 'page-numbers current';
        current.textContent = String(page);
        current.setAttribute('aria-current', 'page');
        current.setAttribute('aria-label', `Page ${page}`);
        current.tabIndex = -1;
        return current;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'page-numbers';
    button.textContent = String(page);
    button.setAttribute('aria-label', `Page ${page}`);
    button.addEventListener('click', () => onSelect(page));
    return button;
}

function createPaginationLink(label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'trademark-search__pagination-link';
    button.textContent = label;
    return button;
}

function focusPaginationTarget(numbers, trigger) {
    if (trigger && !trigger.disabled) {
        trigger.focus();
        return;
    }

    numbers.querySelector('[aria-current="page"]')?.focus();
}

function createPaginationControls(body, matches, batchSize) {
    const pageCount = Math.ceil(matches.length / batchSize);
    let currentPage = 1;

    const controls = document.createElement('nav');
    controls.className = 'trademark-search__pagination';
    controls.setAttribute('aria-label', 'Pagination');

    const bar = document.createElement('div');
    bar.className = 'trademark-search__pagination-bar';

    const previous = createPaginationLink('Previous');
    const next = createPaginationLink('Next');
    const numbers = document.createElement('div');
    numbers.className = 'trademark-search__pagination-numbers';

    const status = document.createElement('p');
    status.className = 'trademark-search__pagination-status';
    status.setAttribute('aria-live', 'polite');

    function updateControls() {
        status.textContent = `Page ${currentPage} of ${pageCount}`;
        previous.disabled = currentPage === 1;
        next.disabled = currentPage === pageCount;
        previous.classList.toggle('is-unavailable', currentPage === 1);
        next.classList.toggle('is-unavailable', currentPage === pageCount);
        numbers.replaceChildren(
            ...getPaginationItems(currentPage, pageCount).map((page) => (
                createPageNumber(page, currentPage, showPage)
            ))
        );
    }

    function showPage(page, trigger) {
        currentPage = Math.min(Math.max(page, 1), pageCount);
        const start = (currentPage - 1) * batchSize;
        body.replaceChildren();
        appendResultRows(body, matches, start, start + batchSize);
        updateControls();
        focusPaginationTarget(numbers, trigger);
    }

    previous.addEventListener('click', () => showPage(currentPage - 1, previous));
    next.addEventListener('click', () => showPage(currentPage + 1, next));

    bar.append(previous, numbers, status, next);
    controls.append(bar);
    // The first page is already in the table, so only the controls need priming.
    updateControls();

    return controls;
}

function createResultsControls(body, matches, content) {
    if (content.resultsDisplayMode === 'limit' || matches.length <= content.resultsPerBatch) {
        return null;
    }

    return content.resultsDisplayMode === 'pagination'
        ? createPaginationControls(body, matches, content.resultsPerBatch)
        : createViewMoreControls(body, matches, content.resultsPerBatch);
}

function createResultsTable(matches, content) {
    const tableWrap = document.createElement('div');
    tableWrap.className = 'trademark-search__table-wrap';

    const table = document.createElement('table');
    table.className = 'trademark-search__table';

    const header = document.createElement('thead');
    const headerRow = document.createElement('tr');
    ['Mark', 'Details', 'Status', 'Class', 'Filed On'].forEach((label) => {
        const heading = document.createElement('th');
        heading.scope = 'col';
        heading.textContent = label;
        headerRow.append(heading);
    });
    header.append(headerRow);

    const body = document.createElement('tbody');
    appendResultRows(body, matches, 0, content.resultsPerBatch);
    table.append(header, body);
    tableWrap.append(table);

    const resultsControls = createResultsControls(body, matches, content);
    if (resultsControls) {
        tableWrap.append(resultsControls);
    }

    if (content.disclaimer) {
        const disclaimer = document.createElement('p');
        disclaimer.className = 'trademark-search__disclaimer';
        disclaimer.textContent = content.disclaimer;
        tableWrap.append(disclaimer);
    }

    return tableWrap;
}

function applySearchParams(url, markLiteral, exactOnly) {
    url.searchParams.set('mark', markLiteral);

    if (exactOnly) {
        url.searchParams.set('exact', '1');
    } else {
        url.searchParams.delete('exact');
    }

    return url.toString();
}

// Markup cached from before this setting existed has no attribute to read.
function parseResultsPerBatch(value) {
    const parsed = Number.parseInt(value, 10);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : DEFAULT_RESULTS_BATCH_SIZE;
}

// Capped results can report either every match found or only the ones on screen.
function getDisplayedTotal(matches, total, content) {
    if (content.resultsDisplayMode !== 'limit' || content.resultsCountBasis !== 'shown') {
        return total;
    }

    return Math.min(matches.length, content.resultsPerBatch);
}

function parseDisplayModes(value) {
    return String(value || '')
        .split(',')
        .filter((mode) => mode === 'desktop' || mode === 'mobile');
}

function addDisplayModeClasses(element, displayModes, className) {
    if (!displayModes.includes('desktop')) {
        element.classList.add(`${className}--mobile-only`);
    }
    if (!displayModes.includes('mobile')) {
        element.classList.add(`${className}--desktop-only`);
    }
}

function createResultsCta(template, searchedMark, options) {
    const {
        displayModes,
        modifier,
        registrationLabel,
        registrationStyle,
        registrationUrl,
        transparent,
    } = options;

    if (!displayModes.length) {
        return null;
    }

    const hasContent = templateHasContent(template);
    const registrationLink = createRegistrationLink(
        registrationUrl,
        registrationLabel,
        registrationStyle,
        searchedMark
    );
    if (!hasContent && !registrationLink) {
        return null;
    }

    const cta = document.createElement('div');
    cta.className = `trademark-search__cta trademark-search__cta--${modifier} wp-block-buttons`;
    if (transparent) {
        cta.classList.add('trademark-search__cta--transparent');
    }
    addDisplayModeClasses(cta, displayModes, 'trademark-search__cta');

    if (hasContent) {
        const ctaCopy = document.createElement('div');
        ctaCopy.append(template.content.cloneNode(true));
        cta.append(ctaCopy);
    }
    if (registrationLink) {
        cta.append(registrationLink);
    }

    return cta;
}

function createResultsLayout(mainContent, searchedMark, content) {
    const layout = document.createElement('div');
    layout.className = 'trademark-search__results-layout';

    const main = document.createElement('div');
    main.className = 'trademark-search__results-main';
    main.append(mainContent);

    const bottomCta = createResultsCta(
        content.unavailableTemplate,
        searchedMark,
        {
            displayModes: content.bottomCtaDisplay,
            modifier: 'matches',
            registrationLabel: content.registrationLabel,
            registrationStyle: content.registrationStyle,
            registrationUrl: content.registrationUrl,
            transparent: content.bottomCtaTransparent,
        }
    );
    if (bottomCta) {
        main.append(bottomCta);
    }
    layout.append(main);

    const stickyCta = createResultsCta(
        content.stickyCtaTemplate,
        searchedMark,
        {
            displayModes: content.stickyCtaDisplay,
            modifier: 'sticky',
            registrationLabel: content.stickyCtaLabel,
            registrationStyle: content.stickyCtaStyle,
            registrationUrl: content.stickyCtaUrl,
            transparent: content.stickyCtaTransparent,
        }
    );
    if (stickyCta) {
        const aside = document.createElement('aside');
        aside.className = 'trademark-search__results-aside';
        addDisplayModeClasses(aside, content.stickyCtaDisplay, 'trademark-search__results-aside');
        if (content.stickyCtaDisplay.includes('desktop')) {
            layout.classList.add('trademark-search__results-layout--with-sticky-desktop');
        }
        aside.append(stickyCta);
        layout.append(aside);
    }

    return layout;
}

function formatResultsCountText(template, total, searchedMark) {
    return template
        .replace(/#/g, () => String(total))
        .replace(/\(TERM\)/g, () => searchedMark);
}

// h2 follows the hero (h1 standalone, h2 embedded) without skipping a level.
function createResultsSectionHeading(className, text) {
    const heading = document.createElement('h2');
    heading.className = className;
    heading.textContent = text;

    return heading;
}

function createMatchResults(matches, total, searchedMark, content) {
    const fragment = document.createDocumentFragment();
    fragment.append(createResultsSectionHeading(
        'trademark-search__results-heading',
        formatResultsCountText(content.resultsCountText, total, searchedMark)
    ));

    if (total > 1 && templateHasContent(content.multipleResultsDescriptionTemplate)) {
        const description = document.createElement('div');
        description.className = 'trademark-search__multiple-results-description';
        description.append(content.multipleResultsDescriptionTemplate.content.cloneNode(true));
        fragment.append(description);
    }

    fragment.append(
        createResultsLayout(
            createResultsTable(matches, content),
            searchedMark,
            content
        )
    );

    return fragment;
}

function createNoMatchResults(searchedMark, content, hasSimilarResults = false) {
    const fragment = document.createDocumentFragment();
    const emptyState = document.createElement('div');
    emptyState.className = 'trademark-search__empty-state';
    if (hasSimilarResults) {
        emptyState.classList.add('trademark-search__empty-state--with-similar');
    }

    const icon = document.createElement('span');
    icon.className = 'trademark-search__success-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '✓';

    emptyState.append(icon);

    if (content.noResultsPreText.trim()) {
        emptyState.append(createResultsSectionHeading(
            'trademark-search__empty-heading',
            `${content.noResultsPreText} “${searchedMark}”`
        ));
    }

    if (templateHasContent(content.noResultsDescriptionTemplate)) {
        const description = document.createElement('div');
        description.className = 'trademark-search__empty-description';
        description.append(content.noResultsDescriptionTemplate.content.cloneNode(true));
        emptyState.append(description);
    }

    fragment.append(
        hasSimilarResults
            ? emptyState
            : createResultsLayout(emptyState, searchedMark, content)
    );

    return fragment;
}

function initializeTrademarkSearch(search) {
    const form = search.querySelector('[data-trademark-search-form]');
    const input = form?.querySelector('[name="mark_literal"]');
    const exactCheckbox = search.querySelector('[data-trademark-search-exact]');
    const submit = form?.querySelector('button[type="submit"]');
    const buttonLabel = form?.querySelector('[data-trademark-search-button-label]');
    const formError = search.querySelector('[data-trademark-search-form-error]');
    const status = search.querySelector('[data-trademark-search-status]');
    const results = search.querySelector('[data-trademark-search-results]');
    const resultsRegion = search.querySelector('.trademark-search__results-region');
    const multipleResultsDescriptionTemplate = search.querySelector(
        '[data-trademark-search-multiple-results-description]'
    );
    const exactResultsDisclaimerTemplate = search.querySelector(
        '[data-trademark-search-exact-results-disclaimer]'
    );
    const noResultsDescriptionTemplate = search.querySelector('[data-trademark-search-no-results-description]');
    const stickyCtaTemplate = search.querySelector('[data-trademark-search-sticky-cta]');
    const unavailableTemplate = search.querySelector('[data-trademark-search-unavailable]');
    const endpoint = search.dataset.endpoint;
    const resultsUrl = search.dataset.resultsUrl;
    const variant = search.dataset.variant;

    if (!form
        || !input
        || !submit
        || !buttonLabel
        || !formError
        || !status
        || !results
        || !resultsRegion
        || !multipleResultsDescriptionTemplate
        || !noResultsDescriptionTemplate
        || !stickyCtaTemplate
        || !unavailableTemplate
        || !endpoint
    ) {
        return;
    }

    const content = {
        bottomCtaDisplay: parseDisplayModes(search.dataset.bottomCtaDisplay),
        bottomCtaTransparent: search.dataset.bottomCtaTransparent === 'true',
        disclaimer: search.dataset.disclaimer,
        exactResultsDisclaimerTemplate,
        multipleResultsDescriptionTemplate,
        noResultsDescriptionTemplate,
        noResultsPreText: search.dataset.noResultsPreText,
        registrationLabel: search.dataset.registrationLabel,
        registrationStyle: search.dataset.registrationStyle,
        registrationUrl: search.dataset.registrationUrl,
        resultsCountBasis: search.dataset.resultsCountBasis,
        resultsCountText: search.dataset.resultsCountText,
        resultsDisplayMode: search.dataset.resultsDisplayMode,
        resultsPerBatch: parseResultsPerBatch(search.dataset.resultsPerBatch),
        stickyCtaLabel: search.dataset.stickyCtaLabel,
        stickyCtaDisplay: parseDisplayModes(search.dataset.stickyCtaDisplay),
        stickyCtaStyle: search.dataset.stickyCtaStyle,
        stickyCtaTemplate,
        stickyCtaTransparent: search.dataset.stickyCtaTransparent === 'true',
        stickyCtaUrl: search.dataset.stickyCtaUrl,
        unavailableTemplate,
    };

    initializeExactSearchTooltip(search);

    function showError(message) {
        formError.textContent = message;
        formError.hidden = false;
        status.textContent = message;
    }

    function clearError() {
        formError.textContent = '';
        formError.hidden = true;
        input.removeAttribute('aria-invalid');
    }

    input.addEventListener('input', clearError);

    async function handleSubmit(event) {
        event.preventDefault();
        clearError();

        const markLiteral = input.value.trim();
        const exactOnly = Boolean(exactCheckbox?.checked);
        if (!markLiteral) {
            input.setAttribute('aria-invalid', 'true');
            showError('Enter a trademark name to search.');
            input.focus();
            return;
        }

        if (variant === 'embedded' && resultsUrl) {
            window.location.assign(applySearchParams(
                new URL(resultsUrl, window.location.origin),
                markLiteral,
                exactOnly
            ));
            return;
        }

        // Put the term in the address bar so these results can be linked to, without
        // reloading the page or adding a history entry the back button can't restore.
        window.history.replaceState(
            window.history.state,
            '',
            applySearchParams(new URL(window.location.href), markLiteral, exactOnly)
        );

        submit.disabled = true;
        buttonLabel.textContent = 'Searching…';
        search.setAttribute('aria-busy', 'true');
        status.textContent = `Searching for ${markLiteral}.`;
        resultsRegion.hidden = true;
        results.hidden = true;
        results.replaceChildren();

        try {
            const response = await fetch(endpoint, {
                body: JSON.stringify({ mark_literal: markLiteral }),
                credentials: 'omit',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                method: 'POST',
            });
            const payload = await response.json().catch(() => null);

            if (!response.ok) {
                throw new Error(payload?.error || 'We couldn’t complete your search. Please try again.');
            }

            if (!payload?.data || !Array.isArray(payload.data.matches)) {
                throw new Error('We couldn’t complete your search. Please try again.');
            }

            const apiMatches = payload.data.matches;
            const { exactMatches, orderedMatches } = prioritizeExactMatches(apiMatches, markLiteral);
            const matches = exactOnly ? exactMatches : orderedMatches;
            const apiTotal = Number.isFinite(Number(payload.data.total))
                ? Number(payload.data.total)
                : apiMatches.length;
            const total = exactOnly ? matches.length : apiTotal;
            const displayedTotal = getDisplayedTotal(matches, total, content);
            let resultContent;

            if (!exactOnly && matches.length && exactMatches.length === 0) {
                resultContent = document.createDocumentFragment();
                resultContent.append(
                    createNoMatchResults(markLiteral, content, true),
                    createMatchResults(matches, displayedTotal, markLiteral, content)
                );
            } else {
                resultContent = matches.length
                    ? createMatchResults(matches, displayedTotal, markLiteral, content)
                    : createNoMatchResults(markLiteral, content);
            }

            const exactDisclaimer = exactOnly
                ? createExactResultsDisclaimer(content.exactResultsDisclaimerTemplate)
                : null;
            results.replaceChildren(...[exactDisclaimer, resultContent].filter(Boolean));
            results.hidden = false;
            resultsRegion.hidden = false;
            if (!exactOnly && matches.length && exactMatches.length === 0) {
                status.textContent = `No exact trademark match found. ${displayedTotal} potential ${displayedTotal === 1 ? 'match' : 'matches'} shown.`;
            } else {
                status.textContent = matches.length
                    ? `${displayedTotal} ${exactOnly ? 'exact ' : ''}trademark ${displayedTotal === 1 ? 'match' : 'matches'} found.`
                    : `No ${exactOnly ? 'exact ' : ''}trademark matches found.`;
            }
        } catch (error) {
            showError(error instanceof Error ? error.message : 'We couldn’t complete your search. Please try again.');
        } finally {
            submit.disabled = false;
            buttonLabel.textContent = 'Search';
            search.removeAttribute('aria-busy');
        }
    }

    form.addEventListener('submit', handleSubmit);

    const initialParams = new URLSearchParams(window.location.search);
    const initialMark = initialParams.get('mark')?.trim();
    if (exactCheckbox && initialParams.get('exact') === '1') {
        exactCheckbox.checked = true;
    }
    if (variant === 'standalone' && initialMark) {
        input.value = initialMark.slice(0, 200);
        form.requestSubmit();
    }
}

document.querySelectorAll('[data-trademark-search]').forEach(initializeTrademarkSearch);
